import { useEffect, useRef } from 'react';
import type { Chapter, Juz } from '@quranjs/api';
import { uniqBy } from 'lodash';
import { BookOpen } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import partsJson from '@/data/parts.json';
import chaptersJson from '@/data/chapters.json';
import partToFirstPage from '@/data/part-to-first-page-id.json';

const parts = partsJson as unknown as Juz[];
const chapters = chaptersJson as unknown as Chapter[];
import { buttonVariants } from '@/shared/components/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/tabs';
import { READER_PAGES_COUNT } from '@/shared/constants/constants';
import { cn } from '@/shared/lib/utils';

const BASE_NAV_PATH = '/page';

const generateNavItems = ({
  parts,
  chapters,
}: {
  parts: Juz[];
  chapters: Chapter[];
}) => {
  const uniqueParts = uniqBy(parts, 'juzNumber');

  return [
    {
      id: 'chapter',
      title: 'Surah',
      children: chapters.map((chapter) => ({
        id: chapter.id,
        title: chapter.nameSimple,
        href: `${BASE_NAV_PATH}/${chapter.pages[0]}`,
      })),
    },
    {
      id: 'part',
      title: 'Juz',
      children: uniqueParts.map((part) => {
        const partNumber =
          part.juzNumber.toString() as keyof typeof partToFirstPage;
        const firstPageOfPart = partToFirstPage[partNumber];

        return {
          id: part.id,
          title: `Juz ${part.juzNumber.toString()}`,
          href: `${BASE_NAV_PATH}/${firstPageOfPart}`,
        };
      }),
    },
    {
      id: 'page',
      title: 'Page',
      children: Array.from({ length: READER_PAGES_COUNT }, (_, i) => ({
        id: i + 1,
        title: `Page ${i + 1}`,
        href: `${BASE_NAV_PATH}/${i + 1}`,
      })),
    },
  ];
};

interface ReaderNavigationTriggerProps {
  isActive: boolean;
  onToggle: () => void;
}

export function ReaderNavigationTrigger({
  isActive,
  onToggle,
}: ReaderNavigationTriggerProps) {
  return (
    <button
      type="button"
      aria-expanded={isActive}
      aria-label="Open reader menu"
      onClick={onToggle}
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-full transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground hover:bg-accent',
      )}
    >
      <BookOpen className="h-5 w-5" />
    </button>
  );
}

interface ReaderNavigationContentProps {
  onNavigate: () => void;
}

export function ReaderNavigationContent({
  onNavigate,
}: ReaderNavigationContentProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const scrollRef = useRef<HTMLElement>(null);

  const navItems = generateNavItems({ parts, chapters });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const active = el.querySelector('[aria-current="page"]');
    if (active) {
      active.scrollIntoView({ block: 'center' });
    }
  }, []);

  return (
    <div className="px-3 py-3">
      <Tabs defaultValue="chapter">
        <TabsList className="w-full">
          {navItems.map((item) => (
            <TabsTrigger key={item.id} value={item.id}>
              {item.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {navItems.map((item) => (
          <TabsContent key={item.id} value={item.id}>
            <nav
              ref={scrollRef}
              className="max-h-[50dvh] overflow-y-auto overscroll-contain grid place-items-center"
            >
              {item.children.map((child) => {
                const isActive = pathname.includes(child.href);
                return (
                  <Link
                    key={child.id}
                    to={child.href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                    className={cn(
                      buttonVariants({ variant: 'ghost' }),
                      'group relative flex h-12 justify-start',
                      'aria-[current]:text-primary aria-[current]:font-semibold',
                    )}
                  >
                    {child.title}
                  </Link>
                );
              })}
            </nav>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
